import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Collapse,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp as ValuationIcon,
  ShowChart as MarketIcon,
  AccountBalance as FinancialIcon,
  Warning as RiskIcon,
  Info as GeneralIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { insightColors, chatTokens, colors } from '../../utils/designSystem';
import type { InsightType } from '../../utils/designSystem';
import type { InsightMetric, InsightAction } from './utils/ToolResultsTransformer';
import reportService from '../../../features/reports/services/reportService';

interface InsightCardProps {
  type: InsightType;
  title: string;
  summary: string;
  metrics?: InsightMetric[];
  rawData?: unknown;
  expandable?: boolean;
  action?: InsightAction;
}

const iconMap: Record<InsightType, React.ElementType> = {
  valuation: ValuationIcon,
  market: MarketIcon,
  financial: FinancialIcon,
  risk: RiskIcon,
  general: GeneralIcon,
};

const InsightCard: React.FC<InsightCardProps> = ({
  type,
  title,
  summary,
  metrics = [],
  rawData,
  expandable = true,
  action,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const accentColor = insightColors[type];
  const Icon = iconMap[type];

  const handleDownload = async () => {
    if (!action || action.type !== 'download') return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await reportService.downloadReport(action.reportId, action.filename);
    } catch (err: any) {
      setDownloadError(err.message || 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const hasDetails = expandable && rawData !== undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          bgcolor: 'background.paper',
          mb: 1.5,
          '&:hover': {
            borderColor: accentColor,
            boxShadow: `0 2px 8px ${accentColor}15`,
          },
          transition: 'all 0.2s ease',
        }}
      >
        {/* Left accent bar */}
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: chatTokens.insight.accentWidth,
            bgcolor: accentColor,
          }}
        />

        {/* Main content */}
        <Box sx={{ pl: `${chatTokens.insight.accentWidth + 16}px`, pr: 2, py: 2 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 1,
                bgcolor: `${accentColor}15`,
              }}
            >
              <Icon sx={{ fontSize: 16, color: accentColor }} />
            </Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                flex: 1,
              }}
            >
              {title}
            </Typography>
            {hasDetails && (
              <IconButton
                size="small"
                onClick={() => setExpanded(!expanded)}
                sx={{
                  width: 28,
                  height: 28,
                  color: 'text.secondary',
                  '&:hover': { bgcolor: `${accentColor}10` },
                }}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Box>

          {/* Summary */}
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              lineHeight: 1.5,
              mb: metrics.length > 0 ? 2 : 0,
            }}
          >
            {summary}
          </Typography>

          {/* Metrics Grid */}
          {metrics.length > 0 && (
            <Grid container spacing={2}>
              {metrics.slice(0, 4).map((metric, index) => (
                <Grid item xs={6} sm={metrics.length <= 2 ? 6 : 4} key={index}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                        display: 'block',
                        mb: 0.25,
                        fontSize: '0.7rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {metric.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: 'text.primary',
                        fontSize: '0.875rem',
                      }}
                    >
                      {metric.value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}

          {/* Download Action */}
          {action?.type === 'download' && (
            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
                onClick={handleDownload}
                disabled={downloading}
                sx={{
                  bgcolor: accentColor,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  '&:hover': { bgcolor: accentColor, filter: 'brightness(0.9)' },
                }}
              >
                {downloading ? 'Downloading...' : 'Download Report'}
              </Button>
              {downloadError && (
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.5 }}>
                  {downloadError}
                </Typography>
              )}
            </Box>
          )}

          {/* Expandable Raw Data */}
          {hasDetails && (
            <Collapse in={expanded}>
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: 'block',
                    mb: 1,
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Raw Data
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    bgcolor: colors.gray[50],
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                    whiteSpace: 'pre-wrap',
                    overflow: 'auto',
                    maxHeight: 200,
                    color: colors.gray[700],
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  {JSON.stringify(rawData, null, 2)}
                </Box>
              </Box>
            </Collapse>
          )}
        </Box>
      </Paper>
    </motion.div>
  );
};

export default InsightCard;
